// ============================================================================
// CheckinQr — renders a check-in URL as a scannable QR code (Pulse-framed).
// ----------------------------------------------------------------------------
// Vendored, offline QR: uses `qrcode.react` (pure React SVG, NO CDN / network).
// The trainer shows this on their device; the client scans it in person and the
// QR opens the PUBLIC /checkin/<token> page (no login). The raw URL is shown
// beneath as a tappable fallback for cameras that can't scan.
//
// SVG (not canvas) so it stays crisp at any size and works under the build's
// CSP without an image element. High error-correction ("M") tolerates a little
// glare/curl when scanned off a phone screen.
// ============================================================================
import { QRCodeSVG } from "qrcode.react";

export interface CheckinQrProps {
  /** The absolute public check-in URL to encode (sessionsApi.checkinQr -> url). */
  url: string;
  /** Pixel size of the QR square. Defaults to a comfortable on-screen scan size. */
  size?: number;
  /** Accessible label for the QR image. */
  label?: string;
}

export function CheckinQr({ url, size = 208, label }: CheckinQrProps) {
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-line">
        <QRCodeSVG
          value={url}
          size={size}
          level="M"
          marginSize={0}
          bgColor="#ffffff"
          fgColor="#1A1C2B"
          role="img"
          aria-label={label ?? url}
        />
      </div>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="max-w-full break-all text-center text-xs font-medium text-body-soft underline-offset-2 hover:text-coral hover:underline focus-visible:outline-none focus-visible:underline"
      >
        {url}
      </a>
    </div>
  );
}
